// Mirrors src/components/tagboard/TagBoardCard.tsx exactly:
//   - Avatar with time-category status dot overlay (bottom-right)
//   - Name (semibold) + Location (uppercase, xs)
//   - Time-ago top-right
//   - Bottom row: tag # · battery % · signal
//   - Alert border (red, 1.5px) when no tag / battery < 30 / disconnected

import React from 'react'
import clsx from 'clsx'
import {Battery, Signal, Tag as TagIcon} from 'lucide-react'
import {Avatar} from '../common/Avatar'
import {
  formatTimeAgo,
  getTimeCategory,
  timeCategoryColor,
} from '../../lib/helpers'

export interface TagBoardCardData {
  id: string
  name: string
  location: string | null
  photo: string | null
  status: 'tagged_in' | 'tagged_out' | 'missing'
  last_tag_in: string | null
  last_tag_out: string | null
  primaryTag?: {
    tagId: string
    batteryLevel: number
    signalStrength: string
    isConnected: boolean
  } | null
}

export const TagBoardCard: React.FC<{item: TagBoardCardData}> = React.memo(({item}) => {
  const isTaggedIn = item.status === 'tagged_in'
  const timeRef = isTaggedIn ? item.last_tag_in : item.last_tag_out
  const dotColor = timeCategoryColor(getTimeCategory(timeRef))

  const tag = item.primaryTag ?? null
  const isAlert = !tag || tag.batteryLevel < 30 || !tag.isConnected
  const infoColor = isAlert ? 'var(--color-statusRed)' : 'var(--color-statusGreen)'
  const iconColor = isAlert ? 'var(--color-textMuted)' : 'var(--color-statusGreen)'
  const tagId = tag?.tagId ? `#${tag.tagId}` : 'Not Tagged'

  return (
    // BorderRadius.lg = 12px (rounded-lg) matches mobile TagBoardCard exactly.
    // px-md py-md = 12px all around — matches mobile's Spacing.md padding.
    <div
      className={clsx(
        'flex flex-col justify-between rounded-lg bg-surfaceCard',
        'px-md py-md min-h-[110px]',
        'border',
        isAlert
          ? 'border-[1.5px] border-alertBorder'
          : 'border-surfaceBorder',
      )}
    >
      {/* Top row: avatar + name/location + time-ago */}
      <div className="mb-md flex items-start justify-between gap-md">
        <div className="flex min-w-0 flex-1 items-center gap-md">
          <div className="relative shrink-0">
            <Avatar uri={item.photo} name={item.name} size={40} />
            <span
              className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2"
              style={{
                backgroundColor: dotColor,
                borderColor: 'var(--color-surfaceCard)',
              }}
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-md font-semibold text-textPrimary">
              {item.name}
            </div>
            <div className="mt-px truncate text-xs uppercase tracking-wider text-textSecondary">
              {item.location ?? '—'}
            </div>
          </div>
        </div>
        <span className="shrink-0 text-xs text-textSecondary">
          {formatTimeAgo(timeRef)}
        </span>
      </div>

      {/* Bottom row: tag # / battery / signal */}
      <div className="flex items-center gap-lg border-t border-divider pt-md">
        <div className="flex items-center gap-1">
          <TagIcon size={12} color={iconColor} />
          <span className="text-xs font-medium" style={{color: infoColor}}>{tagId}</span>
        </div>
        <div className="flex items-center gap-1">
          <Battery size={12} color={iconColor} />
          <span className="text-xs font-medium" style={{color: infoColor}}>
            {tag ? `${tag.batteryLevel}%` : 'N/A'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Signal size={12} color={iconColor} />
          <span className="text-xs font-medium" style={{color: infoColor}}>
            {tag?.signalStrength ?? 'N/A'}
          </span>
        </div>
      </div>
    </div>
  )
})
TagBoardCard.displayName = 'TagBoardCard'
